import { useState, useRef, useEffect } from 'react';
import { Music, Upload, Sparkles, Download, Loader2, Mic, Guitar, Trash2, CheckCircle, XCircle, Settings, Lock, ArrowLeft } from 'lucide-react';

const KaraokeMaker = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [options, setOptions] = useState({
    keepOriginal: false,
    downloadVocal: true,
    downloadInstrumental: true
  });
  const fileInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const CORRECT_PASSWORD = '190800';
  const API_URL = 'https://fbapi.tdidigital.io';

  // Check localStorage on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('karaoke_auth');
    if (savedAuth === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
    }
  }, []);

  // Focus password input on mount
  useEffect(() => {
    if (!isAuthenticated && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [isAuthenticated]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('karaoke_auth', CORRECT_PASSWORD);
      setPasswordError(false);
    } else {
      setPasswordError(true);
      setPassword('');
      if (passwordInputRef.current) {
        passwordInputRef.current.focus();
      }
    }
  };

  const handleFileChange = (file) => {
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/flac', 'audio/ogg', 'audio/x-m4a', 'audio/mp4'];
    const maxSize = 50 * 1024 * 1024;

    if (!allowedTypes.some(type => file.type.includes(type.split('/')[1]))) {
      setError('Format de fichier non supporté');
      return;
    }

    if (file.size > maxSize) {
      setError('Fichier trop volumineux (max 50 MB)');
      return;
    }

    setAudioFile(file);
    setError(null);
    setResult(null);
    setShowOptions(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileChange(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const simulateProgress = () => {
    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += Math.random() * 15;
      if (currentProgress >= 95) {
        clearInterval(interval);
        setProgress(95);
      } else {
        setProgress(currentProgress);
      }
    }, 1000);
    return interval;
  };

  const handleSeparate = async () => {
    if (!audioFile) {
      setError('Aucun fichier sélectionné');
      return;
    }

    if (!options.downloadVocal && !options.downloadInstrumental) {
      setError('Veuillez sélectionner au moins un fichier à télécharger');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(0);

    const progressInterval = simulateProgress();

    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('keep_original', options.keepOriginal.toString());

    try {
      const response = await fetch(`${API_URL}/api/karaoke/separate`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la séparation');
      }

      const data = await response.json();

      clearInterval(progressInterval);
      setProgress(100);
      setResult(data);
      setLoading(false);

    } catch (err) {
      clearInterval(progressInterval);
      setLoading(false);
      setError(err.message || 'Erreur lors de la séparation');
      console.error('Erreur:', err);
    }
  };

  const downloadFile = (type) => {
    if (!result) return;
    const url = `${API_URL}${result.files[type].url}`;
    window.open(url, '_blank');
  };

  const cleanupFiles = async () => {
    if (!result) return;
    try {
      await fetch(`${API_URL}/api/karaoke/cleanup/${result.task_id}`, {
        method: 'DELETE'
      });
      setResult(null);
      setAudioFile(null);
      setShowOptions(false);
    } catch (err) {
      console.error('Erreur lors du nettoyage:', err);
    }
  };

  const resetForm = () => {
    setResult(null);
    setAudioFile(null);
    setError(null);
    setProgress(0);
    setShowOptions(false);
    setOptions({
      keepOriginal: false,
      downloadVocal: true,
      downloadInstrumental: true
    });
  };

  const getProgressStage = () => {
    if (progress < 25) return 'Upload du fichier...';
    if (progress < 50) return 'Analyse audio...';
    if (progress < 80) return 'Séparation IA...';
    return 'Finalisation...';
  };

  // Password Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex items-center justify-center p-4">
        {/* Background Animation */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-md w-full relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full mb-6 relative">
              <Lock className="w-12 h-12 text-white" />
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full blur-xl opacity-50 animate-pulse" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">
                Zone Protégée
              </span>
            </h1>
            <p className="text-gray-300">Entrez le mot de passe pour accéder au Karaoké Maker</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
            <div className="space-y-6">
              <div>
                <input
                  ref={passwordInputRef}
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError(false);
                  }}
                  placeholder="Mot de passe"
                  className={`w-full px-6 py-4 bg-white/10 border ${passwordError ? 'border-red-500' : 'border-white/20'} rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all text-center text-lg`}
                  autoComplete="off"
                />
                {passwordError && (
                  <div className="mt-3 flex items-center justify-center space-x-2 text-red-400">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm">Mot de passe incorrect</span>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all transform shadow-2xl shadow-purple-500/50 flex items-center justify-center space-x-2"
              >
                <Lock className="w-5 h-5" />
                <span>Déverrouiller</span>
              </button>
            </div>
          </form>

          <div className="mt-8 text-center">
            <a
              href="/"
              className="text-gray-400 hover:text-white transition-colors text-sm inline-flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Retour à l'accueil</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Main Karaoke Maker Interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black pt-20 pb-20">
      {/* Background Animation */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-between mb-6">
            <a
              href="/"
              className="text-white/70 hover:text-white transition-colors text-sm inline-flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Retour à l'accueil</span>
            </a>
            <button
              onClick={() => {
                localStorage.removeItem('karaoke_auth');
                setIsAuthenticated(false);
              }}
              className="text-white/70 hover:text-white transition-colors text-sm flex items-center space-x-1"
            >
              <Lock className="w-4 h-4" />
              <span>Verrouiller</span>
            </button>
          </div>

          <div className="inline-block mb-6 px-6 py-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-400/30 rounded-full backdrop-blur-sm">
            <span className="text-purple-300 font-semibold flex items-center space-x-2">
              <Sparkles className="w-4 h-4" />
              <span>IA Audio Avancée</span>
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">
              Karaoké Maker
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Séparez les voix et l'instrumental de n'importe quelle chanson en quelques clics
          </p>
        </div>

        {/* Main Content */}
        {!result && !loading && (
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Upload Zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`relative bg-white/5 backdrop-blur-xl rounded-3xl p-12 border-2 border-dashed transition-all duration-300 ${
                isDragging
                  ? 'border-purple-400 bg-purple-500/10 scale-105'
                  : 'border-white/20 hover:border-purple-400/50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={(e) => e.target.files && handleFileChange(e.target.files[0])}
                className="hidden"
              />

              <div className="text-center">
                {audioFile ? (
                  <div className="space-y-6">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl mb-4">
                      <Music className="w-12 h-12 text-white" />
                    </div>
                    <div>
                      <p className="text-white text-lg font-semibold mb-2">Fichier sélectionné</p>
                      <p className="text-purple-300 font-medium">{audioFile.name}</p>
                      <p className="text-gray-400 text-sm mt-2">
                        {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl mb-4 backdrop-blur-sm">
                      <Upload className="w-12 h-12 text-purple-300" />
                    </div>
                    <div>
                      <p className="text-white text-xl font-semibold mb-2">
                        Glissez votre fichier audio ici
                      </p>
                      <p className="text-gray-400 mb-4">ou</p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg shadow-purple-500/50"
                      >
                        <Upload className="w-5 h-5" />
                        <span>Choisir un fichier</span>
                      </button>
                      <p className="text-gray-500 text-sm mt-4">MP3, WAV, FLAC, OGG, M4A (Max 50 MB)</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Options Panel */}
            {showOptions && audioFile && (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
                <div className="flex items-center space-x-3 mb-6">
                  <Settings className="w-6 h-6 text-purple-400" />
                  <h3 className="text-2xl font-bold text-white">Options de séparation</h3>
                </div>

                <div className="space-y-4">
                  {/* Keep Original */}
                  <label className="flex items-start space-x-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={options.keepOriginal}
                      onChange={(e) => setOptions({ ...options, keepOriginal: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-900"
                    />
                    <div className="flex-1">
                      <p className="text-white font-semibold">Conserver le fichier original</p>
                      <p className="text-gray-400 text-sm mt-1">Garder une copie du fichier uploadé</p>
                    </div>
                  </label>

                  {/* Download Vocal */}
                  <label className="flex items-start space-x-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={options.downloadVocal}
                      onChange={(e) => setOptions({ ...options, downloadVocal: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-900"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="text-white font-semibold">Télécharger les voix</p>
                        <Mic className="w-4 h-4 text-pink-400" />
                      </div>
                      <p className="text-gray-400 text-sm mt-1">Obtenir le fichier vocal séparé</p>
                    </div>
                  </label>

                  {/* Download Instrumental */}
                  <label className="flex items-start space-x-4 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 hover:border-blue-500/50 transition-colors cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={options.downloadInstrumental}
                      onChange={(e) => setOptions({ ...options, downloadInstrumental: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-900"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="text-white font-semibold">Télécharger l'instrumental</p>
                        <Guitar className="w-4 h-4 text-blue-400" />
                        <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">
                          Recommandé pour karaoké
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm mt-1">Obtenir le fichier instrumental (pour karaoké)</p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Action Button */}
            {audioFile && showOptions && (
              <div className="text-center">
                <button
                  onClick={handleSeparate}
                  disabled={!options.downloadVocal && !options.downloadInstrumental}
                  className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 text-white px-12 py-5 rounded-2xl font-bold text-xl hover:scale-105 transition-all transform shadow-2xl shadow-purple-500/50 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <Sparkles className="w-6 h-6 group-hover:rotate-180 transition-transform duration-500" />
                  <span>Séparer Audio</span>
                </button>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex items-center space-x-3">
                  <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                  <div>
                    <p className="text-red-200 font-semibold">Erreur</p>
                    <p className="text-red-300 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10">
              <div className="text-center space-y-6">
                <div className="relative inline-block">
                  <div className="w-32 h-32 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center animate-pulse">
                    <Loader2 className="w-16 h-16 text-white animate-spin" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full blur-2xl opacity-50 animate-ping" />
                </div>

                <div>
                  <p className="text-white text-2xl font-bold mb-2">Séparation en cours...</p>
                  <p className="text-purple-300 text-lg">{getProgressStage()}</p>
                </div>

                <div className="w-full bg-gray-700/50 rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-full transition-all duration-500 relative overflow-hidden"
                    style={{ width: `${progress}%` }}
                  >
                    <div className="absolute inset-0 bg-white/30 animate-pulse" />
                  </div>
                </div>
                <p className="text-gray-400 text-sm">{Math.round(progress)}%</p>

                <div className="grid grid-cols-3 gap-4 pt-6">
                  {[Mic, Guitar, Music].map((Icon, i) => (
                    <div
                      key={i}
                      className="bg-white/5 rounded-xl p-4 backdrop-blur-sm"
                    >
                      <Icon className="w-8 h-8 text-purple-400 mx-auto animate-bounce" style={{ animationDelay: `${i * 0.3}s` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="max-w-5xl mx-auto space-y-8">
            <div className="text-center bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-3xl p-8 backdrop-blur-sm">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-2">Séparation terminée!</h2>
              <p className="text-gray-300">Vos fichiers sont prêts</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {options.downloadVocal && (
                <div className="group bg-gradient-to-br from-pink-500/10 to-purple-500/10 backdrop-blur-xl rounded-3xl p-8 border border-pink-500/30 hover:border-pink-500/60 transition-all hover:scale-105 transform">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-500 rounded-2xl flex items-center justify-center">
                        <Mic className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">Voix</h3>
                        <p className="text-gray-400">{result.files.vocal.size_mb} MB</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadFile('vocal')}
                    className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:from-pink-700 hover:to-purple-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
                  >
                    <Download className="w-5 h-5" />
                    <span>Télécharger</span>
                  </button>
                </div>
              )}

              {options.downloadInstrumental && (
                <div className="group bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-xl rounded-3xl p-8 border border-blue-500/30 hover:border-blue-500/60 transition-all hover:scale-105 transform">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                        <Guitar className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">Instrumental</h3>
                        <p className="text-gray-400">{result.files.instrumental.size_mb} MB</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadFile('instrumental')}
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-cyan-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
                  >
                    <Download className="w-5 h-5" />
                    <span>Télécharger</span>
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={resetForm}
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all backdrop-blur-sm border border-white/20"
              >
                Nouvelle séparation
              </button>
              <button
                onClick={cleanupFiles}
                className="px-8 py-4 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl font-semibold transition-all backdrop-blur-sm border border-red-500/30 flex items-center justify-center space-x-2"
              >
                <Trash2 className="w-5 h-5" />
                <span>Supprimer les fichiers du serveur</span>
              </button>
            </div>

            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex items-start space-x-3">
                <Sparkles className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-amber-300 font-bold mb-1">💡 Astuce Pro</p>
                  <p className="text-gray-300">L'instrumental peut être utilisé pour créer des karaoké avec paroles synchronisées!</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KaraokeMaker;